import isEmpty from 'lodash.isempty';
import React from 'react';
import { Box, Grid, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';

import Loading from '../../containers/Loading';
import api from '../../actions/EndPoints';
import { adviceProspectusBaseUrl } from '../shared/Links';
import PdfLinks from '../shared/PdfLinks';
import GenericPanel from './GenericPanel';
import { PsBeaconControl, PsBeaconControlGroup, PsBeaconError } from '../shared/BeaconRegister';
import { setLastRequestTime, showModal } from '../../Utils';
import { dispatchOverlayLoad } from '../shared/Analytics';
import sortBy from 'lodash.sortby';
import axios from 'axios';
import connectWithUserStore from '../../reducers/connectWithUserStore';
import { ProspectusesContent, adviceFundsList, etfTickers } from './ConsentContent';
import { GENERAL_HUB_ERROR_MODAL, HUB_GENERAL_ERROR_OVERLAY } from '../../Constants';

const ProspectusPanel = ({
    prospectusesDisplay,
    includeHeader,
    panelNum,
    showCheckbox,
    headerText = 'Prospectuses for funds that can be recommended through the Service',
    children,
    state,
    checkError,
    handleChange,
    open,
}) => {
    const [ADVFundsList, setADVFundsList] = React.useState([]);
    const [EtfFundsList, setEtfFundsList] = React.useState([]);
    const options = {
        headers: {
            Accept: 'application/json',
            'X-TRP-Requested-with': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            apiKey: `${state.apiKey}`,
        },
    };
    const getFundListData = () => {
        axios
            .post(
                api.prospectusesAPI,
                {
                    query: `query MultipleMutualFundQuery($tickers: [String]!)
                    {mutualFunds(tickers: $tickers){ name ticker cusip trpCode } allExchangeTradedFunds { name shortName ticker cusip trpCode}}`,

                    variables: {
                        tickers: adviceFundsList,
                    },
                },
                options
            )
            .then((response) => {
                // mutual funds
                const adviceFundsListData = response.data ? response.data.data.mutualFunds : [];
                const sortAdvFundsList = sortBy(adviceFundsListData, (o) => o.name);

                const advFundsList = sortAdvFundsList.map((obj) => ({
                    ...obj,
                    advProspectusURL: adviceProspectusBaseUrl + obj.cusip,
                    displayName: obj.name.replace('T. Rowe Price ', ''),
                }));
                setADVFundsList(advFundsList);

                // exchange traded funds
                const exchangeTradedFundsListData = response.data
                    ? response.data.data.allExchangeTradedFunds
                    : [];
                const filterExchangeTradedFundsList = exchangeTradedFundsListData.filter((fund) =>
                    etfTickers.includes(fund.ticker)
                );
                const sortExchangeTradedFundsList = sortBy(
                    filterExchangeTradedFundsList,
                    (o) => o.name
                );
                const exchangeTradedFundsList = sortExchangeTradedFundsList.map((obj) => ({
                    ...obj,
                    advProspectusURL: adviceProspectusBaseUrl + obj.cusip,
                }));
                setEtfFundsList(exchangeTradedFundsList);
            })
            .catch((error) => {
                const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
                showErrorModalPromise
                    .then(() => console.log(error))
                    .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY));
            })
            .then(() => setLastRequestTime());
    };

    React.useEffect(() => {
        if (prospectusesDisplay) {
            getFundListData();
        }
    }, [prospectusesDisplay]);

    return (
        <GenericPanel
            headerText={
                <>
                    <span>
                        {panelNum}. {headerText}
                    </span>
                    {checkError?.checkedProspectuses && (
                        <Box>
                            <PsBeaconError id="prospectuses-field-error">
                                Please fill in all required fields to continue.
                            </PsBeaconError>
                        </Box>
                    )}
                </>
            }
            id="prospectuses-container"
            open={open}>
            {includeHeader && (
                <Typography
                    id={ProspectusesContent.headerId}
                    type="body" loose scale={100} bold
                    sx={{ paddingBottom: '40px' }}
                    dangerouslySetInnerHTML={{
                        __html: ProspectusesContent.header,
                    }}
                />
            )}

            <Typography
                id="prospectuses-detail-text"
                type="body" scale={100} light
                sx={{ paddingBottom: '40px' }}
                dangerouslySetInnerHTML={{
                    __html: ProspectusesContent.subHeader,
                }}
            />
            {(isEmpty(ADVFundsList) || isEmpty(EtfFundsList)) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loading headerText="Preparing Prospectuses..." />
                </Box>
            )}
            {prospectusesDisplay && !isEmpty(ADVFundsList) && !isEmpty(EtfFundsList) && (
                <>
                    <Typography
                        loose bold
                        sx={{ paddingBottom: '16px' }}>
                        Mutual funds
                    </Typography>
                    <Grid container id="prospectuses-links-container" sx={{ width: '100%' }}>
                        {ADVFundsList.map((pdfLink) => (
                            <PdfLinks
                                key={pdfLink.name}
                                pdfLink={pdfLink.advProspectusURL}
                                fundName={pdfLink.displayName}
                                noIcon
                                pdfTitle
                                linkStyle
                                addLinkStyle
                                showPdfIcon
                            />
                        ))}
                    </Grid>
                    <Typography
                        loose bold
                        sx={{ paddingBottom: '16px', paddingTop: '32px' }}>
                        Exchange-traded funds
                    </Typography>
                    <Grid
                        container
                        id="prospectuses-links-container"
                        sx={{ justifyContent: 'space-between' }}>
                        {EtfFundsList.map((pdfLink) => (
                            <PdfLinks
                                key={pdfLink.name}
                                pdfLink={pdfLink.advProspectusURL}
                                fundName={pdfLink.shortName}
                                noIcon
                                pdfTitle
                                linkStyle
                                addLinkStyle
                                showPdfIcon
                            />
                        ))}
                    </Grid>
                </>
            )}
            {children}
            {showCheckbox && (
                <Box id="prospectuses-checkbox-container">
                    <PsBeaconControlGroup>
                        <PsBeaconControl
                            id="checkedProspectuses-control"
                            inline
                            style={{ paddingBottom: '24px', paddingTop: '40px' }}>
                            <input
                                required
                                type="checkbox"
                                name="checkedProspectuses"
                                id="checkedProspectuses"
                                onChange={handleChange && handleChange('checkedProspectuses')}
                            />
                            <label
                                slot="label"
                                id="checkedProspectuses-label"
                                htmlFor="checkedProspectuses">
                                {ProspectusesContent.checkboxLabel}
                            </label>
                            <div slot="error">This field is required.</div>
                        </PsBeaconControl>
                    </PsBeaconControlGroup>
                </Box>
            )}
        </GenericPanel>
    );
};

export default connectWithUserStore(ProspectusPanel);
